import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError } from '@cortex/faro';
import { config } from '@/config';

interface ErrorBody {
  error?: {
    message?: string;
    code?: string;
  };
}

// --- 型定義（バックエンド shared/schemas/common.ts と手動同期） ---

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business QAG グループ KGI（退会率・ladies・月次）を表す型
 * @graph-connects none
 */
export interface KgiMetrics {
  churnRate: number | null;
  churnCountMonthly: number | null;
  churnCountDoubleRental: number | null;
  activeMembersBase: number | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business チーム CRM 機能の指標（退会者数・オンボ成功率・ロイヤル成功率）を表す型
 * @graph-connects none
 */
export interface CrmMetrics {
  churnCount: number | null;
  onboardingRate: number | null;
  onboardingCohortWeek: string | null;
  onboardingCohortSize: number | null;
  loyaltyRate: number | null;
  loyaltyCohortWeek: string | null;
  loyaltyCohortSize: number | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business チーム CS 機能の指標（KPI=初動SLO・回答SLO・3ターム解決率・解決率、モニタリング=AI完遂率・満足度）を表す型
 * @graph-connects none
 */
export interface CsMetrics {
  csat: number | null;
  firstResponseSlaRate: number | null;
  answerSlaRate: number | null;
  threeTurnRate: number | null;
  resolutionRate: number | null;
  aiCompletionRate: number | null;
  inquiryCount: number | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business QAグループKPIメトリクスを表す型。KGI(月次)・CRM(月次)・CS(日次)・詳細を集約
 * @graph-connects none
 */
export interface KpiMetrics {
  date: string;
  month: string;
  kgi: KgiMetrics;
  crm: CrmMetrics;
  cs: CsMetrics;
  styling: Record<string, number | null> | null;
  service: Record<string, number | null> | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business KPIトレンド時系列の各データ点を表す型。granularity は指標ごとのネイティブ粒度で、日次(CS)・週次(オンボ成功率/ロイヤル成功率/退会者数)・月次(退会率/退会者数)の3種
 * @graph-connects none
 */
export interface KpiTrendItem {
  date: string;
  granularity: 'day' | 'week' | 'month';
  churnRate: number | null;
  churnCount: number | null;
  onboardingRate: number | null;
  loyaltyRate: number | null;
  csat: number | null;
  firstResponseSlaRate: number | null;
  answerSlaRate: number | null;
  threeTurnRate: number | null;
  resolutionRate: number | null;
  aiCompletionRate: number | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 日次品質レポートサマリーを表す型
 * @graph-connects none
 */
export interface DailyReport {
  report_date: string;
  headline: string;
  body: string;
  highlights: string[];
  concerns: string[];
  generated_by: 'gemini' | 'rule';
}

// --- API エラー ---

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business Quality Atelier APIクライアント。認証・ダッシュボードの各エンドポイントを呼び出し
 * @graph-connects config [calls] API URL設定参照
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// --- リクエスト関数 ---

/** @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す */
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${config.apiUrl}${path}`;
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (options?.headers) {
    const extra = new Headers(options.headers);
    extra.forEach((value, key) => {
      headers.set(key, value);
    });
  }
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (response.status === 401 && response.headers.get('X-Edge-Auth-Start')) {
    // Edge Auth Worker からの 401 → OAuth start にフルページ遷移
    window.location.href = buildEdgeAuthStartUrl(window.location.href);
  }

  if (!response.ok) {
    const body = (await response.json().catch((error: unknown) => {
      // エラーレスポンス本体が JSON でない場合は HTTP ステータスのみで報告する
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'error_response_body_parse_failed',
      });
      return {} as ErrorBody;
    })) as ErrorBody;
    throw new ApiError(
      body.error?.message ?? `Request failed: ${String(response.status)}`,
      response.status,
      body.error?.code,
    );
  }

  return response.json() as Promise<T>;
}

// --- Auth API ---
/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 認証・ログイン・ログアウト用APIクライアント
 * @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す
 */
export const authApi = {
  getAuthUrl: () => request<{ authUrl: string }>('/auth/login'),
  getCurrentUser: () =>
    request<{
      user: {
        id: string;
        email: string;
        name: string;
        picture?: string;
      };
      expiresAt: string;
    }>('/auth/me'),
  logout: () => request<{ success: boolean }>('/auth/logout', { method: 'POST' }),
};

// --- Dashboard API ---
/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business ダッシュボードのKPI・概況・異常検知取得用APIクライアント
 * @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す
 */
export const dashboardApi = {
  getKpi: (date?: string) =>
    request<{ data: KpiMetrics | null }>(`/dashboard/kpi${date ? `?date=${date}` : ''}`),
  getKpiTrend: (days = 730) =>
    request<{ data: KpiTrendItem[] }>(`/dashboard/kpi/trend?days=${String(days)}`),
  getSummary: (date?: string) =>
    request<{ data: DailyReport | null }>(`/dashboard/summary${date ? `?date=${date}` : ''}`),
};

// --- Output（プロジェクトアウトプット）型定義（バックエンド shared/schemas/output.ts と手動同期） ---

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 案件の目的カテゴリ。Backlogに項目が無いためcortex側の分類マスタで持つ
 * @graph-connects none
 */
export type OutputCategory =
  | 'new_feature'
  | 'improvement'
  | 'ops_efficiency'
  | 'platform'
  | 'incident'
  | 'regulatory'
  | 'investigation'
  | 'external'
  | 'unclassified';

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アウトプット集計の期間の刻み。yearはエアクロの会計年度（7月始まり）
 * @graph-connects none
 */
export type OutputPeriod = 'month' | 'quarter' | 'year';

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 1期間のカテゴリ別内訳を表す型
 * @graph-connects none
 */
export interface OutputCategoryBreakdown {
  category: OutputCategory;
  releaseCount: number;
  totalPt: number;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 1期間のリリース実績を表す型。Pt未入力件数は数字の信頼度を示す
 * @graph-connects none
 */
export interface OutputPeriodBucket {
  key: string;
  label: string;
  releaseCount: number;
  totalPt: number;
  avgPt: number | null;
  /** その期に1件以上リリースした主担当のユニーク人数。一人当たりの分母 */
  ownerCount: number;
  /** 主担当が空の件数。記入漏れではなく分割チケット（_chunk）の運用によるもの。分子にだけ入るが、実測では一人当たりへの影響は小さい */
  ownerMissingCount: number;
  ptPerOwner: number | null;
  releasesPerOwner: number | null;
  ptMissingCount: number;
  byCategory: OutputCategoryBreakdown[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 必須項目の記入漏れ1件を表す型
 * @graph-connects none
 */
export interface OutputHygieneItem {
  issueKey: string;
  summary: string;
  team: string | null;
  status: string;
  pt: number | null;
  daysSinceUpdate: number | null;
  missingFields: string[];
  /** 意図的に保留している案件か。放置と混ぜないための区別 */
  onHold: boolean;
  holdReason: string | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 仕掛かり（未リリース）案件の全体像を表す型
 * @graph-connects none
 */
export interface OutputWip {
  count: number;
  totalPt: number;
  ptMissingCount: number;
  onHoldCount: number;
  byField: { field: string; count: number }[];
  items: OutputHygieneItem[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business プロジェクトアウトプット画面が扱う集計結果全体を表す型
 * @graph-connects none
 */
export interface OutputSummary {
  generatedAt: string;
  period: OutputPeriod;
  buckets: OutputPeriodBucket[];
  wip: OutputWip;
  releasedDateMissing: OutputHygieneItem[];
  /** リリース完了日が未来日・極端に古い日の案件。集計に入らない誤入力 */
  invalidReleaseDates: OutputHygieneItem[];
}

// --- Output API ---
/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business プロジェクトアウトプット集計の取得用APIクライアント
 * @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す
 */
export const outputApi = {
  getSummary: (period: OutputPeriod) =>
    request<{ data: OutputSummary }>(`/output/summary?period=${period}`),
};

// --- VoC（Voice of Customer）型定義（バックエンド shared/schemas/voc.ts と手動同期） ---

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 論点1件の実測件数を表す型。週をまたいで追えるよう key を安定させる
 * @graph-connects none
 */
export interface VocThemeCount {
  key: string;
  label: string;
  count: number;
  /** 前週の件数。新規に出てきた論点は null */
  prevCount: number | null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoCレポートの実測部分を表す型。機械が数えた数字だけを持つ
 * @graph-connects none
 */
export interface VocMeasured {
  inquiryCount: number;
  prevInquiryCount: number | null;
  themes: VocThemeCount[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 論点1件の所見を表す型。観察→仮説→インパクト→アクションの順で、確度を必ず持つ
 * @graph-connects none
 */
export interface VocFinding {
  themeKey: string;
  observation: string;
  hypothesis: string;
  impact: string;
  recommendation: string;
  owner: string;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoCレポートの生成部分を表す型。LLMが書いた文章だけをここに閉じ込める
 * @graph-connects none
 */
export interface VocAnalysis {
  headline: string;
  findings: VocFinding[];
  shifts: string[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 週次VoCレポート1本を表す型。measured（実測）と analysis（生成）を分けて持つ
 * @graph-connects none
 */
export interface VocWeeklyReport {
  weekStart: string;
  weekEnd: string;
  generatedAt: string;
  /** 'mock' はサンプル。画面はこれを見てサンプル表示に切り替える */
  source: 'mock' | 'generated';
  measured: VocMeasured;
  analysis: VocAnalysis;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoC件数推移の1点を表す型
 * @graph-connects none
 */
export interface VocWeekPoint {
  weekStart: string;
  inquiryCount: number;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoCレポート画面が扱う応答全体を表す型
 * @graph-connects none
 */
export interface VocWeeklyResponse {
  report: VocWeeklyReport | null;
  trend: VocWeekPoint[];
}

// --- VoC API ---
/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoC週次レポートの取得用APIクライアント
 * @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す
 */
export const vocApi = {
  getWeekly: (weeks = 12) =>
    request<{ data: VocWeeklyResponse }>(`/voc/weekly?weeks=${String(weeks)}`),
};

// --- アイテム確認依頼 型定義（バックエンド shared/schemas/item-incident.ts と手動同期） ---

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼の着手区分。未着手=シートのステータス空欄、完了=完了、それ以外の確認中系=対応中
 * @graph-connects none
 */
export type ItemIncidentStatusCategory = 'notStarted' | 'inProgress' | 'done';

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business インシデント対応シートのアイテム確認依頼1件。シートの該当行URLを持つ
 * @graph-connects none
 */
export interface ItemIncidentRow {
  rowNumber: number;
  no: string;
  requestedOn: string | null;
  requester: string;
  service: string;
  brand: string;
  skuId: string;
  kind: string;
  bodyExcerpt: string;
  checker: string;
  status: string;
  statusCategory: ItemIncidentStatusCategory;
  priority: string;
  url: string;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼の一覧レスポンス
 * @graph-connects none
 */
export interface ItemIncidentList {
  generatedAt: string;
  sheetUrl: string;
  rows: ItemIncidentRow[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 回答時に選べるステータス。シートのステータス列のプルダウンと同じ値
 * @graph-connects none
 */
export type ItemIncidentAnswerStatus = 'バイイング確認中' | 'ブランド確認中' | 'CS確認中' | '完了';

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼1件の詳細。確認内容の全文と回答・進捗・対応期日を持つ
 * @graph-connects none
 */
export interface ItemIncidentDetail extends ItemIncidentRow {
  patternId: string;
  body: string;
  answer: string;
  progress: string;
  dueDate: string;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 詳細画面のレスポンス。ログイン中の人が回答できるか、回答がSlackで依頼者に届くかを持つ
 * @graph-connects none
 */
export interface ItemIncidentDetailResponse {
  row: ItemIncidentDetail;
  canAnswer: boolean;
  answererName: string | null;
  notifiesRequester: boolean;
  statusOptions: ItemIncidentAnswerStatus[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼への回答の送信内容
 * @graph-connects none
 */
export interface ItemIncidentAnswerInput {
  answer: string;
  status: ItemIncidentAnswerStatus;
}

// --- アイテム確認依頼 API ---
/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼の一覧・詳細の取得と回答の送信を行うAPIクライアント
 * @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す
 */
export const itemIncidentApi = {
  getList: () => request<{ data: ItemIncidentList }>('/item-incident/list'),
  getDetail: (rowNumber: number) =>
    request<{ data: ItemIncidentDetailResponse }>(`/item-incident/rows/${String(rowNumber)}`),
  answer: (rowNumber: number, input: ItemIncidentAnswerInput) =>
    request<{ data: ItemIncidentDetailResponse }>(
      `/item-incident/rows/${String(rowNumber)}/answer`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
};
